import {
  CheckCircleIcon,
  ClockIcon,
  PencilIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Box } from '@/components/ui/v3/Box';
import { TreatmentRecordRequestAuditLogSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { useGetUserByIdQuery } from '@/services/api/userService';

import { RequestStatusBadge } from '../request/badges/RequestStatusBadge';
import { UserAvatar } from '../user/UserAvatar';

interface ActivityLogItemProps {
  auditLog: TreatmentRecordRequestAuditLogSchema;
  isLast: boolean;
}

const getEventIcon = (action?: string, outcome?: string) => {
  // Determine color based on outcome
  let colorClass = 'text-gray-500'; // default
  switch (outcome) {
    case 'success':
      colorClass = 'text-green-500';
      break;
    case 'failure':
      colorClass = 'text-red-500';
      break;
    case 'pending':
      colorClass = 'text-yellow-500';
      break;
    case 'not_applicable':
    default:
      // Use action-specific colors for not_applicable or undefined outcomes
      switch (action) {
        case 'change_status':
          colorClass = 'text-blue-500';
          break;
        case 'agent_run':
          colorClass = 'text-green-500';
          break;
        case 'human_update':
          colorClass = 'text-indigo-500';
          break;
        default:
          colorClass = 'text-gray-500';
      }
  }

  // Select icon based on action
  switch (action) {
    case 'change_status':
      return <PencilIcon className={`h-4 w-4 ${colorClass}`} />;
    case 'agent_run':
      return <CheckCircleIcon className={`h-4 w-4 ${colorClass}`} />;
    case 'human_update':
      return <UserIcon className={`h-4 w-4 ${colorClass}`} />;
    default:
      return <ClockIcon className={`h-4 w-4 ${colorClass}`} />;
  }
};

const getEventDescription = (
  auditLog: TreatmentRecordRequestAuditLogSchema,
): React.ReactNode => {
  if (auditLog.action) {
    switch (auditLog.action) {
      case 'change_status':
        return (
          <span className="flex items-center gap-1.5">
            changed status from{' '}
            <RequestStatusBadge status={auditLog.previous_status} />
            to <RequestStatusBadge status={auditLog.new_status} />
          </span>
        );
      case 'agent_run':
        return 'performed agent action';
      case 'human_update':
        return 'updated the request';
      default:
        return `performed ${auditLog.action}`;
    }
  }

  return 'recorded activity';
};

const formatEventTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Unknown time';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid date';

    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return 'Invalid date';
  }
};

const formatFullDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Unknown time';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid date';

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

export const ActivityLogItem: React.FC<ActivityLogItemProps> = React.memo(
  ({ auditLog, isLast }) => {
    const description = getEventDescription(auditLog);
    const eventTime = auditLog.created_at;
    const action = auditLog.action || 'activity';
    const { outcome } = auditLog;

    // Fetch user data for display name when it's a user action
    const { data: user } = useGetUserByIdQuery(auditLog.user_id || '', {
      skip: auditLog.actor !== 'user' || !auditLog.user_id,
    });

    const actorName =
      auditLog.actor === 'user' && user
        ? `${user.first_name} ${user.last_name}`.trim() || 'User'
        : 'System';

    return (
      <li className="relative">
        {/* Timeline connector */}
        {!isLast && (
          <div className="absolute left-6 top-12 w-px h-6 bg-gray-200 z-0" />
        )}

        {/* Box container */}
        <Box className="relative shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start gap-x-4">
            {/* Event icon */}
            <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
              {getEventIcon(action, outcome)}
            </div>

            {/* Event content */}
            <div className="flex-auto">
              <div className="flex justify-between gap-x-4">
                <div className="flex items-start gap-2">
                  <UserAvatar
                    userId={auditLog.actor === 'user' ? auditLog.user_id : null}
                  />
                  <div className="text-xs leading-5 text-gray-500">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-gray-900">
                        {actorName}
                      </span>
                      {description}
                    </div>
                    {auditLog.details && (
                      <div className="mt-2 text-xs text-gray-600 italic whitespace-pre-wrap">
                        &ldquo;{auditLog.details}&rdquo;
                      </div>
                    )}
                  </div>
                </div>
                <time
                  dateTime={eventTime || ''}
                  className="flex-none text-xs leading-5 text-gray-500"
                  title={formatFullDateTime(eventTime)}
                >
                  {formatEventTime(eventTime)}
                </time>
              </div>
            </div>
          </div>
        </Box>
      </li>
    );
  },
);

ActivityLogItem.displayName = 'ActivityLogItem';
